import { postDraftAiSchema, postDraftUserSchema, type PetitionDraft } from '#src/app/shared/api/petitionDraft';

import type { SapLandingStep } from './types';

/**
 * Draft axis — which phase the draft was in when it decided the step:
 * `post_draft` (completed petition), `pre_draft` (in progress), `unparseable` (malformed → intro fallback).
 */
export type DraftState = 'post_draft' | 'pre_draft' | 'unparseable';

/** A resolved draft landing: the step to enter on, plus which phase the draft was in. */
type DraftLanding = { landingStep: SapLandingStep; draftState: DraftState; parseException?: unknown };

/** A completed petition lands on its review/edit step; `null` means "not a completed draft". */
function resolvePostDraftLanding(draft: PetitionDraft): DraftLanding | null {
	const schema = draft.type === 'user_draft' ? postDraftUserSchema : postDraftAiSchema;
	if (!schema.safeParse(draft).success) return null;

	return { landingStep: draft.type === 'user_draft' ? 'create' : 'preview', draftState: 'post_draft' };
}

/** An in-progress (pre-)draft lands on its next input step. The experiment variant lives here. */
function resolvePreDraftLanding(draft: PetitionDraft, { isNewDraft }: { isNewDraft: boolean }): DraftLanding {
	const draftState = 'pre_draft';

	// A carried-over issue prompt only skips the chooser on a fresh start (`new_draft`). A returning
	// user with a leftover prompt starts over at the chooser.
	if (isNewDraft && draft.type === 'ai_draft' && draft.input?.issuePrompt) {
		return { landingStep: 'personal-story', draftState };
	}

	return { landingStep: 'intro', draftState };
}

/**
 * Draft axis — the draft decides its landing: post-draft (completed) first, else pre-draft. The single
 * try/catch lives here: an unexpectedly malformed draft falls back to `intro`, is flagged `unparseable`,
 * and surfaces `parseException` for the shell to report.
 */
export function resolveDraftLanding(draft: PetitionDraft, { isNewDraft }: { isNewDraft: boolean }): DraftLanding {
	try {
		return resolvePostDraftLanding(draft) ?? resolvePreDraftLanding(draft, { isNewDraft });
	} catch (parseException) {
		return { landingStep: 'intro', draftState: 'unparseable', parseException };
	}
}
